import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Receipt, Split, DollarSign, X } from 'lucide-react';
import { posOrders } from '../../api/posAPI';
import usePosStore from '../../stores/posStore';
import BillingModal from './BillingModal';
import SplitBillModal from './SplitBillModal';
import CloseShiftModal from './CloseShiftModal';

export default function CashierDashboard() {
  const { posToken, activeShift, clearPosSession } = usePosStore();
  const queryClient = useQueryClient();
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showBilling, setShowBilling] = useState(false);
  const [showSplit, setShowSplit] = useState(false);
  const [showCloseShift, setShowCloseShift] = useState(false);

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['pos-bill-queue', posToken],
    queryFn: async () => {
      const res = await posOrders.list(posToken, { status: 'billed' });
      return res.data.orders || [];
    },
    enabled: !!posToken,
    refetchInterval: 10000,
  });

  const billQueue = data || [];

  const handlePaid = (paidOrder) => {
    setShowBilling(false);
    setSelectedOrder(null);
    refetch();
  };

  const handleSplit = (result) => {
    setShowSplit(false);
    setSelectedOrder(null);
    refetch();
  };

  return (
    <div className="h-full flex flex-col p-4 gap-4">
      {/* Bill Queue */}
      <div className="flex-1 bg-white rounded-xl shadow-sm overflow-hidden flex flex-col">
        <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
          <h2 className="font-semibold text-gray-900 flex items-center gap-2">
            <Receipt className="h-5 w-5 text-blue-600" />
            Bill Queue
            <span className="bg-blue-600 text-white text-xs px-2 py-0.5 rounded-full">{billQueue.length}</span>
          </h2>
          <button onClick={() => refetch()} className="text-xs text-blue-600 hover:underline">
            Refresh
          </button>
        </div>

        {isLoading ? (
          <div className="flex-1 flex items-center justify-center text-sm text-gray-500">Loading…</div>
        ) : billQueue.length === 0 ? (
          <div className="flex-1 flex items-center justify-center text-sm text-gray-400">
            No orders waiting for payment
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto divide-y divide-gray-100">
            {billQueue.map((order) => (
              <div
                key={order._id}
                onClick={() => setSelectedOrder(order)}
                className={`px-4 py-3 flex items-center justify-between cursor-pointer hover:bg-gray-50 transition-colors ${
                  selectedOrder?._id === order._id ? 'bg-blue-50' : ''
                }`}
              >
                <div>
                  <p className="font-medium text-gray-900">{order.orderNo}</p>
                  <p className="text-xs text-gray-500">
                    {order.type === 'dine-in' ? `Table: ${order.tableName}` : 'Takeout'} •{' '}
                    {order.waiterName}
                  </p>
                  <p className="text-xs text-gray-500">{order.items?.filter(i => !i.voidedAt).length} items</p>
                </div>
                <div className="text-right">
                  <p className="font-semibold text-gray-900">₱{(order.totalAmount / 100).toFixed(2)}</p>
                  <span className="text-xs text-amber-600 bg-amber-50 px-2 py-0.5 rounded-full">Billed</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Selected order actions */}
      {selectedOrder && (
        <div className="bg-white rounded-xl shadow-sm p-4 space-y-2">
          <div className="flex items-center justify-between">
            <span className="font-medium text-gray-900">Order {selectedOrder.orderNo}</span>
            <button onClick={() => setSelectedOrder(null)}>
              <X className="h-4 w-4 text-gray-400" />
            </button>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setShowBilling(true)}
              className="flex-1 bg-green-600 hover:bg-green-700 text-white font-semibold py-2 rounded-lg flex items-center justify-center gap-1 text-sm"
            >
              <DollarSign className="h-4 w-4" />
              Bill ₱{(selectedOrder.totalAmount / 100).toFixed(2)}
            </button>
            <button
              onClick={() => setShowSplit(true)}
              className="flex-1 bg-blue-100 hover:bg-blue-200 text-blue-800 font-semibold py-2 rounded-lg flex items-center justify-center gap-1 text-sm"
            >
              <Split className="h-4 w-4" />
              Split Bill
            </button>
          </div>
        </div>
      )}

      {/* Close shift */}
      <div className="bg-white rounded-xl shadow-sm p-4">
        <button
          onClick={() => setShowCloseShift(true)}
          className="w-full bg-red-600 hover:bg-red-700 text-white font-semibold py-2.5 rounded-lg text-sm"
        >
          Close Shift & Print Z Reading
        </button>
      </div>

      {showCloseShift && (
        <CloseShiftModal
          onClose={() => setShowCloseShift(false)}
          onClosed={() => setShowCloseShift(false)}
        />
      )}

      {showBilling && selectedOrder && (
        <BillingModal
          isOpen={showBilling}
          order={selectedOrder}
          onClose={() => setShowBilling(false)}
          onPaid={handlePaid}
        />
      )}
      {showSplit && selectedOrder && (
        <SplitBillModal
          order={selectedOrder}
          onClose={() => setShowSplit(false)}
          onSplit={handleSplit}
        />
      )}
    </div>
  );
}
